// DEPTH TILE FACES — the SINGLE source of truth for the Bevel + 3D effect
// "icons" (owner, 2026-07-20: "we have really cool icons showing what each
// effect does. I want the same icons for 3d and bevel. Then I want those
// icons to be in the second bar as buttons with their chips").
//
// MECHANISM — matched EXACTLY to the house effect tiles. EffectTiles.jsx
// renders each effect as a CSS-styled `Aa` span (`cce-fx-tile-preview` + the
// plain style object `effectDefs.tilePreview()` returns: textShadow /
// -webkit-text-stroke). Pure CSS, instant, zero images. These two faces are
// the same idiom — an `Aa` span carrying a CSS look — so Bevel/3D read in the
// same visual language as Shadow/Lift/Glow/… and never drift from it.
//
// WHY CSS AND NOT A REAL MINI-RENDER (bevel) OR A 3D BAKE (extrude):
//   · Matching the tiles means matching their mechanism — an `Aa` SPAN, not an
//     <img>. The house preview services (embossPreviewRenderer.renderEmbossPreview,
//     stylePreviewRenderer) exist and are pure-2D for bevel, BUT they are
//     async, return image URLs and pull fabric — a 30px Row-2 face would need a
//     loading/placeholder state and a different DOM shape than the tiles.
//   · 3D's only "real" render is the three.js extrude bake, which the boot law
//     forbids at editor start; a lazy runtime render + cache is heavy and — in
//     this no-testing/no-screenshot lane — unverifiable by eye before shipping.
//   · CSS text-shadow expresses both looks honestly: Bevel = a raised chamfer
//     (NW highlight edge + SE shadow edge over a steel face); 3D = an extruded
//     block (a stepped stack of same-direction offsets from a light top face
//     down to a colored side wall, plus a soft ground shadow). Both stay in the
//     tiles' cool/slate palette.
// Net: zero new imports, no async, no three.js — boot-law compliant by
// construction, and one module feeds BOTH surfaces (Row 2 + the drawer) so
// there are no twins.
//
// SIZING — every offset is in `em`, so ONE style object is correct at any size:
// the consumer sets font-size only (Row 2 face `.cce-depth-face` = 15px to sit
// in the 30px button grid; the drawer bumps it via `.cce-depth-toggle`). The
// look never has to be re-authored per surface.
import React from 'react';

// Self-contained face look (family/weight/shadows inline) so a face renders
// correctly even where only one of the two stylesheets is present (e.g. the
// ribbon lab, which loads ribbon.css but not StudioCoreEditor.css). CSS owns
// font-SIZE only — see the class rules in ribbon.css / StudioCoreEditor.css.
const FACE_BASE = {
  display: 'inline-block',
  fontFamily: 'Lexend, sans-serif',
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '-0.02em',
};

// kind → the CSS look. Keyed by the ENGINE fx step id family: 'bevel' (fxBevel)
// and 'extrude' (fxExtrude / the "3D" control), so callers pass what they
// already know.
export const DEPTH_FACE_STYLES = {
  // Bevel — a raised, chamfered steel face: crisp NW highlight edge + SE
  // chamfer edge, then a deeper SE shadow and a soft lift. The highlight/shadow
  // pair is what reads as "beveled" rather than merely drop-shadowed.
  bevel: {
    color: '#c4ccd6',
    textShadow: [
      '-0.03em -0.03em 0 #ffffff',
      '0.03em 0.03em 0 #7c8593',
      '0.06em 0.06em 0.02em rgba(15,23,42,0.55)',
      '0 0.09em 0.11em rgba(0,0,0,0.4)',
    ].join(', '),
  },
  // 3D — an extruded block: a light cool-tinted top face over a stepped indigo
  // side wall (the classic long-shadow extrusion), landing on a soft ground
  // shadow. The colored wall keeps it distinct from the neutral bevel at a
  // glance.
  extrude: {
    color: '#eef2ff',
    textShadow: [
      '0.03em 0.03em 0 #93a7f2',
      '0.06em 0.06em 0 #7c93ee',
      '0.09em 0.09em 0 #6780e9',
      '0.12em 0.12em 0 #536ee2',
      '0.15em 0.15em 0 #405cd6',
      '0.18em 0.2em 0.16em rgba(20,25,60,0.5)',
    ].join(', '),
  },
};

export const DEPTH_FACE_LABELS = { bevel: 'Bevel', extrude: '3D' };

/**
 * The Bevel / 3D face — a styled `Aa` (the house effect-tile idiom).
 * @param {'bevel'|'extrude'} kind  which face
 * @param {string} className        variant hook for font-size (Row 2 vs drawer)
 * @param {string} [title]          optional tooltip (the button/label usually
 *                                  carries its own; the face stays aria-hidden)
 */
export default function DepthTileFace({ kind, className = '', title }) {
  const look = DEPTH_FACE_STYLES[kind];
  if (!look) return null;
  return (
    <span
      className={`cce-depth-face cce-depth-face-${kind}${className ? ` ${className}` : ''}`}
      style={{ ...FACE_BASE, ...look }}
      title={title}
      aria-hidden="true"
    >
      Aa
    </span>
  );
}
